import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { Chip, Button } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import {
  useRewardsEngine,
  useCreateRewardConfig,
  useUpdateRewardConfig,
  useArchiveRewardConfig,
} from "@/hooks/useRewardsEngine";
import { usePageStore } from "@/hooks/usePageStore";
import RewardsConfigModal, {
  RewardsConfigFormData,
} from "@/ui/modals/rewardsConfig/RewardsConfigurationModal";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import {
  RewardConfigurationType,
  RewardsEngine,
} from "@/services/rewards-engine.service";
import { useAppTheme } from "@/utils/theme";

const RewardsConfigPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const {
    data: rewardsEngineList,
    isLoading,
    isFetching,
    refetch: refetchRewards,
  } = useRewardsEngine(true);
  const { mutateAsync: createRewardConfig, isPending: isCreating } =
    useCreateRewardConfig();
  const { mutateAsync: updateRewardConfig, isPending: isUpdating } =
    useUpdateRewardConfig();
  const { mutateAsync: archiveRewardConfig, isPending: isArchiving } =
    useArchiveRewardConfig();

  const [modalState, setModalState] = useState<{
    open: boolean;
    rewardConfig?: RewardsEngine | null;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    rewardConfig?: RewardsEngine;
  }>({ open: false });

  const handleOpenCreate = () =>
    setModalState({ open: true, rewardConfig: null });
  const handleOpenEdit = (rewardConfig: RewardsEngine) =>
    setModalState({ open: true, rewardConfig });
  const handleCloseModal = () =>
    setModalState({ open: false, rewardConfig: null });

  const currentUserId = userInfo?._id;

  const handleSave = async (data: RewardsConfigFormData) => {
    const {
      fixedLadderConfiguration,
      programmableLadderConfiguration,
      invalidResult,
      validValue,
      invalidValue,
      ...rest
    } = data;

    const cleanedData = {
      ...rest,
      ...(data.rewardConfigurationType ===
        RewardConfigurationType.FixedLadder && {
        fixedLadderConfiguration,
      }),
      ...(data.rewardConfigurationType ===
        RewardConfigurationType.ProgrammableLadder && {
        programmableLadderConfiguration: programmableLadderConfiguration
          ? [programmableLadderConfiguration]
          : undefined,
      }),
      ...(data.rewardBasedOnResult && {
        invalidResult,
        validValue,
        invalidValue,
      }),
      accountId: userInfo?.accountId,
      orgUnitIds: data.orgUnitIds,
    };

    try {
      const editing = modalState.rewardConfig;
      if (editing?._id) {
        await updateRewardConfig({
          id: editing._id,
          data: { ...cleanedData, updatedBy: currentUserId },
        });
        showSnackBar("Rewards configuration updated.", "success");
      } else {
        await createRewardConfig({
          ...cleanedData,
          createdBy: currentUserId,
        });
        showSnackBar("Rewards configuration created.", "success");
      }
      handleCloseModal();
    } catch (error) {
      showSnackBar("Error saving reward config: " + error);
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.rewardConfig;
    if (!target) return;
    try {
      await archiveRewardConfig(target._id);
      showSnackBar("Rewards configuration deleted.", "success");
      setDeleteAlert({ open: false });
      refetchRewards();
    } catch (error) {
      showSnackBar("Error deleting reward config: " + error);
    }
  };

  const rows = useMemo(() => {
    if (!rewardsEngineList || rewardsEngineList.length === 0) return [];

    return rewardsEngineList.map((reward, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(reward),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => setDeleteAlert({ open: true, rewardConfig: reward }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: { color: theme.palette.error.main },
        },
      ];

      const row = createDataRow(index, {
        eventType: reward.eventType,
        event: reward.event,
        rewardConfigurationType: reward.rewardConfigurationType,
        configurationDetails: (
          <Chip
            label={`Ladder Steps: ${
              reward.fixedLadderConfiguration
                ? reward.fixedLadderConfiguration.length
                : reward.programmableLadderConfiguration
                  ? "Programmable Ladder"
                  : "No Details"
            }`}
          />
        ),
        actions: <ActionMenu id={reward._id} options={actionMenu} />,
      });
      return row;
    });
  }, [rewardsEngineList, theme]);

  const headCells: HeadCell[] = [
    {
      id: "eventType",
      isSortable: true,
      disablePadding: false,
      label: "Event Type",
      align: "left",
      width: "18%",
    },
    {
      id: "event",
      isSortable: true,
      disablePadding: false,
      label: "Event",
      align: "left",
      width: "18%",
    },
    {
      id: "rewardConfigurationType",
      isSortable: true,
      disablePadding: false,
      label: "Configuration Type",
      align: "left",
      width: "18%",
    },
    {
      id: "configurationDetails",
      isSortable: false,
      disablePadding: false,
      label: "Details",
      align: "left",
      width: "28%",
    },
    {
      id: "actions",
      isSortable: false,
      disablePadding: true,
      label: "Actions",
      align: "center",
      width: "18%",
    },
  ];

  useEffect(() => {
    setPageTitle("Rewards Configuration");
  }, []);

  const isSavingModal = isCreating || isUpdating;

  return (
    <Box className="w-full overflow-y-auto">
      <div className="full-width-container mx-auto">
        <div className="flex flex-col items-center justify-start min-h-screen">
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenCreate}
            >
              Create New Configuration
            </Button>
          </Box>

          <Card sx={{ width: "100%", borderRadius: "8px" }}>
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <DataTable
                data={rows}
                headCells={headCells}
                order="asc"
                orderBy="event"
                rowsPerPage={10}
                page={0}
                total={rewardsEngineList?.length || 0}
                isLoading={isLoading || isFetching}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {modalState.open && (
        <RewardsConfigModal
          open={modalState.open}
          onClose={handleCloseModal}
          onSave={handleSave}
          isPending={isSavingModal}
          rewardConfig={modalState.rewardConfig}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={isArchiving}
        />
      )}
    </Box>
  );
};

export default RewardsConfigPage;
